import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  ErrorInfo,
} from "react";
import { Outlet, useMatch } from "react-router";
import { createLogger } from "debug/DebugingLogger";
import { axiosInstance, storageServiceApi } from "contexts/axiosApiInstance";
import { useNavigation } from "contexts/NavigationProvider";
import { useAuth } from "contexts/AuthProvider";
import { installActivityTracker } from "@/components/hooks/activityGate";
import { Sidebar } from "./utils/Sidebar";
import { OrgDeletionBanner } from "./organizations/OrgDeletionBanner";
import { SupportPanel } from "./support/SupportPanel";
import { useTeamsStore } from "./stores/teamsStore";
import { useMainStateStore } from "./stores/mainStateStore";
import { useOrgContextStore } from "./stores/orgContextStore";
import { useTokensStore } from "./stores/tokensStore";
import { useAppInitializationStore } from "./stores/appInitializationStore";
import { useShallow } from "zustand/react/shallow";
import { LoadingState } from "@/components/common/LoadingState";
import { useOnlineStatus } from "@/components/hooks/useOnlineStatus";
const log = createLogger("MainLayout");

class DebugErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    log.error(
      "CAUGHT: %s COMPONENT_STACK: %s",
      error.message,
      errorInfo.componentStack,
    );
  }
  render() {
    if (this.state.hasError) {
      setTimeout(() => this.setState({ hasError: false }), 100);
      return null;
    }
    return this.props.children;
  }
}

export const MainLayout = () => {
  useEffect(() => {
    installActivityTracker();
  }, []);
  // The space filter is the Overview's own state and the rail lights the row
  // to say so, so leaving the page must not leave a filter armed behind a lit
  // row on a page that does not narrow by it.
  const onOverview = useMatch("/overview") !== null;
  const setSelectedSpace = useOrgContextStore((s) => s.setSelectedSpace);
  useEffect(() => {
    if (!onOverview) setSelectedSpace(null);
  }, [onOverview, setSelectedSpace]);

  const refreshTeams = useNavigation();
  const { authenticated } = useAuth();
  const { fetchTeamsList } = useTeamsStore(
    useShallow((state) => ({ fetchTeamsList: state.fetchTeamsList })),
  );
  const { fetchModelsList } = useMainStateStore(
    useShallow((state) => ({ fetchModelsList: state.fetchModelsList })),
  );
  const { fetchTokensList } = useTokensStore(
    useShallow((state) => ({ fetchTokensList: state.fetchTokensList })),
  );
  const {
    modelsInitialized,
    teamsInitialized,
    tokensInitialized,
    isAppInitialized,
  } = useAppInitializationStore(
    useShallow((state) => ({
      modelsInitialized: state.modelsInitialized,
      teamsInitialized: state.teamsInitialized,
      tokensInitialized: state.tokensInitialized,
      isAppInitialized: state.isAppInitialized(),
    })),
  );

  const isOnline = useOnlineStatus();
  const [bannerState, setBannerState] = useState<
    "none" | "offline" | "restored"
  >("none");
  const prevOnlineRef = useRef(isOnline);

  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;

    if (!isOnline) {
      setBannerState("offline");
    } else if (!wasOnline) {
      // Just came back online — show brief restored banner
      setBannerState("restored");
      const timer = setTimeout(() => setBannerState("none"), 1500);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => JSON.parse(localStorage.getItem("sidebarOpen") || "false") as boolean,
  );
  const leftSidebarRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const initializationStartedRef = useRef(false);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebarOpen", JSON.stringify(newState));
      return newState;
    });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && isSidebarOpen) {
        setIsSidebarOpen(false);
        localStorage.setItem("sidebarOpen", "false");
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen]);

  // Published on :root (not the layout div) because dialogs portal to
  // document.body and still want to center over the CONTENT area, not the
  // viewport. Cleared on unmount so canvas routes don't inherit a stale width.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--main-sidebar-width",
      isSidebarOpen
        ? "var(--sidebar-width-open)"
        : "var(--sidebar-width-closed)",
    );
    return () => {
      document.documentElement.style.removeProperty("--main-sidebar-width");
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!navigator.onLine) return;
    axiosInstance
      .get("/action/users/me")
      .then((res) => {
        if (res.data) {
          log.info("User ID found in the response %s", res.data.user_id);
          localStorage.setItem("clientID", res.data.user_id);
          localStorage.setItem("email", res.data.email);
          storageServiceApi
            .post("init_storage_profile")
            .then(() => log.info("Storage profile initialized successfully"))
            .catch((error) =>
              log.error("Failed to initialize storage profile: %o", error),
            );
        } else {
          log.error("User ID not found in the response");
        }
      })
      .catch((error) => log.error("Error fetching user profile: %o", error));
  }, []);

  useEffect(() => {
    if (!authenticated || !isOnline) return;
    // Skip if already fully initialized
    if (modelsInitialized && teamsInitialized && tokensInitialized) return;
    // Guard against duplicate concurrent init (reset when online restores)
    if (initializationStartedRef.current && !isAppInitialized) {
      // Previous attempt failed (offline) — allow retry now that we're online
      initializationStartedRef.current = false;
    }
    if (initializationStartedRef.current) return;
    initializationStartedRef.current = true;

    const initPromises: Promise<unknown>[] = [];
    if (!modelsInitialized) {
      initPromises.push(
        fetchModelsList({ showLoading: false }).catch((error) =>
          log.error("Failed to initialize models: %o", error),
        ),
      );
    }
    if (!teamsInitialized) {
      initPromises.push(
        fetchTeamsList({ showLoading: false }).catch((error) =>
          log.error("Failed to initialize teams: %o", error),
        ),
      );
    }
    if (!tokensInitialized) {
      initPromises.push(
        fetchTokensList({ showLoading: false }).catch((error) =>
          log.error("Failed to initialize tokens: %o", error),
        ),
      );
    }
    Promise.all(initPromises).catch((error) =>
      log.error("Error during app initialization: %o", error),
    );
  }, [
    authenticated,
    isOnline,
    isAppInitialized,
    modelsInitialized,
    teamsInitialized,
    tokensInitialized,
    fetchModelsList,
    fetchTeamsList,
    fetchTokensList,
  ]);

  // The sidebar needs none of this data, so it stays up while the stores fill —
  // only the content column waits.
  const isBooting = !isAppInitialized && authenticated;

  return (
    <div className="flex w-full h-screen overflow-hidden relative">
      <div
        className="h-full shrink-0 overflow-hidden"
        style={{
          width: isSidebarOpen ? "240px" : "48px",
          transition:
            "width var(--sidebar-collapse-duration) var(--sidebar-collapse-ease)",
        }}
        ref={leftSidebarRef}
      >
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      </div>
      <div className="flex flex-col flex-1 min-w-0 min-h-0 bg-bg @container">
        <OrgDeletionBanner />
        {bannerState === "offline" && (
          <div className="offline-banner" role="status" aria-live="polite">
            <div className="banner-dot" />
            <span>You're offline</span>
          </div>
        )}
        {bannerState === "restored" && (
          <div
            className="offline-banner restored"
            role="status"
            aria-live="polite"
          >
            <div className="banner-check" />
            <span>Connected</span>
          </div>
        )}
        {isBooting ? (
          <div className="flex-1 min-h-0 flex items-center justify-center">
            {isOnline ? (
              <LoadingState size="large" isLoading={true} label="Loading..." />
            ) : (
              <div
                className="offline-empty-state"
                role="status"
                aria-live="polite"
              >
                <div className="empty-dot" />
                <div className="empty-text">You&apos;re offline</div>
                <div className="empty-sub">
                  Your data will appear when the connection restores
                </div>
              </div>
            )}
          </div>
        ) : (
          <DebugErrorBoundary>
            <Outlet
              context={{
                headerRef,
                sidebarRef: leftSidebarRef,
                refreshTeams,
                isMobile: window.innerWidth < 1024,
                toggleSidebar,
              }}
            />
          </DebugErrorBoundary>
        )}
      </div>
      <SupportPanel />
    </div>
  );
};
